import { HttpError, request } from "@/shared/http/client";
import type { TurnStep } from "../api/use-agents";

/**
 * O que o agente preparou e que só vale depois de um clique — lido dos PASSOS.
 *
 * O protocolo é do back (`approval.ports.ts`): uma ferramenta devolve
 * `{ approval: {kind, id, text, payload} }` no retorno, que já é gravado nos
 * passos do turno. Por isso o cartão nasce igual ao vivo e depois de um F5, sem
 * evento novo no fio.
 */
export interface ChatApproval {
	readonly kind: string;
	readonly id: string;
	readonly text: string;
	readonly payload?: Readonly<Record<string, unknown>>;
}

/**
 * O que cada `kind` sabe fazer. Um tipo novo de aprovação é UMA entrada aqui e
 * o retorno da ferramenta que a pede — o cartão e a bolha não mudam.
 */
export type ApprovalState = "pending" | "approved" | "discarded";

interface Approver {
	/** Em que pé está? Para o cartão reaberto não oferecer o botão outra vez. */
	state(approval: ChatApproval): Promise<ApprovalState>;
	approve(approval: ChatApproval): Promise<void>;
	/** As listas que mudam com a aprovação, para quem as tiver abertas. */
	readonly refresh: readonly (readonly string[])[];
}

const APPROVERS: Record<string, Approver> = {
	knowledge: {
		state: async (a) => {
			try {
				const { gap } = await request<{ gap: { promoted: boolean } }>(`/v3/memory/gaps/${a.id}`);
				return gap.promoted ? "approved" : "pending";
			} catch (e) {
				if (isDiscarded(e)) return "discarded";
				throw e;
			}
		},
		approve: async (a) => {
			await request(`/v3/memory/gaps/${a.id}/approve`, {
				method: "POST",
				body: {
					destination: a.payload?.destination === "library" ? "library" : "curated",
					text: a.text,
				},
			});
		},
		refresh: [
			["memory", "gaps"],
			["memory", "curated"],
		],
	},
};

/** A proposta já não existe: alguém a descartou na Memória (ou apagou a pergunta). */
export function isDiscarded(e: unknown): boolean {
	return (
		e instanceof HttpError &&
		e.status === 404 &&
		(e.payload as { error?: string } | undefined)?.error === "NO_SUCH_GAP"
	);
}

export function approverOf(kind: string): Approver | null {
	return APPROVERS[kind] ?? null;
}

/** As aprovações do turno, na ordem em que foram pedidas; só as que esta tela sabe aprovar. */
export function approvalsFrom(steps: readonly TurnStep[]): ChatApproval[] {
	const found: ChatApproval[] = [];
	for (const step of steps) {
		if (step.isError || !step.output?.startsWith("{")) continue;
		try {
			const approval = (JSON.parse(step.output) as { approval?: ChatApproval }).approval;
			if (approval && typeof approval.id === "string" && approverOf(approval.kind))
				found.push(approval);
		} catch {
			// Um retorno que não é JSON não é uma aprovação — e nunca quebra a conversa.
		}
	}
	return found;
}
